import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import Dialog from '@mui/material/Dialog';

import TileImageComponent from './components/TileImageComponent.jsx';
import TileQuizComponent from './components/TileQuizComponent.jsx';
import AnonymousBannerComponent from './components/AnonymousBannerComponent.jsx';
import ShareTileComponent from './components/ShareTileComponent.jsx';
import Attachments from '../Attachments/Attachments.jsx';
import LoadingComponent from '../LoadingComponent/LoadingComponent.jsx';
import ProgressBarComponent from '../ProgressBarComponent/ProgressBarComponent.jsx';
import {
  RoundedTileHolder,
  HeaderText,
  Header,
  TileContainer,
  TilePreview,
  Container,
  TilePreviewBlock,
  TileViewer,
  ModalContainer,
  FullImage,
  AttachmentsWrapper,
  ProgressBarWrapper,
} from './StyledComponents';
import CreateAccount from '../CreateAccount/CreateAccount.jsx';
import { tilesProps } from '../types';
import TileTextsContainer from './components/TileTextsContainer.jsx';

import {
  DirectionalButtonLeft,
  DirectionalButtonRight,
  ArrowWrapper,
} from './components/DirectionalButtons.jsx';

const FullSizeTileLoadingContainer = () => (
  <TileViewer className="viewer">
    <TileContainer>
      <TilePreview id="tile_preview_section">
        <TilePreviewBlock className="large-centered columns clearfix tile_preview_block">
          <div className="tile_holder" style={{ width: '100%' }}>
            <FullImage className="loading" />
            <div
              className="tile_main"
              style={{ marginTop: '20%', marginBottom: '25%' }}
            >
              <LoadingComponent />
            </div>
          </div>
        </TilePreviewBlock>
      </TilePreview>
    </TileContainer>
  </TileViewer>
);

const TileOptsBar = ({ userData, tileActions, tile }) => {
  const [headerText, setHeaderText] = useState('Copy to Board');

  const copyTile = () => {
    setHeaderText('Copied');
    tileActions.copyTile(tile);
  };

  if (userData.isGuestUser || userData.isEndUser) {
    return null;
  }

  return (
    <div>
      <Header className="tile_preview_menu">
        <li className="preview_menu_item">
          <a className="copy_to_board" onClick={() => copyTile()}>
            <HeaderText>{headerText}</HeaderText>
          </a>
        </li>
      </Header>
    </div>
  );
};

const sanitizeClassList = classList => {
  const result = [];
  for (let i = 0; i < classList.length; i++) {
    result.push(classList[i]);
  }
  return result;
};

const parseClickTarget = target => {
  const sanitizedClassList = sanitizeClassList(target.classList);
  const attachmentClicked =
    sanitizedClassList.indexOf('attachment-filename') > -1 ||
    sanitizedClassList.indexOf('icon-tile-attachment') > -1 ||
    sanitizedClassList.indexOf('tile-attachment-inner') > -1 ||
    sanitizedClassList.indexOf('attachment-link') > -1;
  if (attachmentClicked) {
    if (sanitizedClassList.indexOf('attachment-link') > -1) {
      return target;
    }
    if (sanitizedClassList.indexOf('tile-attachment-inner') > -1) {
      return target.parentElement;
    }
    if (
      sanitizedClassList.indexOf('attachment-filename') > -1 ||
      sanitizedClassList.indexOf('icon-tile-attachment') > -1
    ) {
      return target.parentElement.parentElement;
    }
  }
  return target;
};

const checkIfLinkClick = (e, tileId, trackLinkClick) => {
  const target = parseClickTarget(e.target);
  if (target.tagName === 'A' && target.getAttribute('href')) {
    trackLinkClick(target, tileId);
  }
};

const wrapper = {
  className: {
    explore:
      'reveal-modal standard_modal small tile_previews explore-tile_previews tile_previews-show explore-tile_previews-show bg-user-side',
  },
  style: {
    complete: {},
    incomplete: {},
    explore: {
      display: 'block',
      opacity: '1',
      visibility: 'visible',
      top: '0px',
      background: '#FCF7F5',
    },
  },
};

const FullSizeTile = ({
  tile,
  loading,
  nextTile,
  prevTile,
  closeTile,
  tileOrigin,
  userData,
  tileActions,
  submitAnswer,
  trackLinkClick,
  shouldConvertGuestAsUser,
  isFirstTile = false,
  isLastTile = false,
  arrowLeftRef,
  arrowRightRef,
}) => {
  const { completedTiles } = useSelector(state => state.progressBarData);
  const [imageTop, setImageTop] = useState('120px');
  const swipeHandlers = useSwipeable({
    onSwipedRight: prevTile,
    onSwipedLeft: nextTile,
    delta: 100,
  });
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const showProgressBar = !['explore', 'edit', 'campaign'].includes(tileOrigin);

  useEffect(() => {
    if (completedTiles === 2 && shouldConvertGuestAsUser) {
      setOpenCreateModal(true);
    }
  }, [completedTiles, shouldConvertGuestAsUser]);

  return (
    <ModalContainer
      className={wrapper.className[tileOrigin]}
      style={wrapper.style[tileOrigin]}
    >
      <Dialog open={openCreateModal} onClose={() => setOpenCreateModal(false)}>
        {openCreateModal && (
          <CreateAccount
            origin="tileCompletion"
            setOpenCreateModal={setOpenCreateModal}
          />
        )}
      </Dialog>
      <div className={tileOrigin === 'explore' ? 'modal_container' : ''}>
        {tileOrigin === 'explore' && (
          <div className="modal_header">
            <i
              onClick={closeTile}
              className="close-reveal-modal stickable fa-regular fa-xmark"
              style={{ marginLeft: '360px', top: '12px' }}
            />
          </div>
        )}
        {tile && showProgressBar && (
          <div>
            <ProgressBarWrapper>
              <ProgressBarComponent campaignUuid={tile.campaignUuid} />
            </ProgressBarWrapper>
          </div>
        )}
        <Container
          className={
            tileOrigin === 'explore' ? 'modal_content' : 'container row'
          }
        >
          {loading ? (
            <FullSizeTileLoadingContainer />
          ) : (
            <>
              <TileViewer {...swipeHandlers} className="viewer">
                <TileContainer>
                  <TilePreview id="tile_preview_section">
                    {tileOrigin === 'explore' && (
                      <TileOptsBar
                        userData={userData}
                        tileActions={tileActions}
                        tile={tile}
                      />
                    )}
                    <TilePreviewBlock className="large-centered columns clearfix tile_preview_block">
                      <RoundedTileHolder
                        className="tile_holder"
                        aria-live="polite"
                        style={{ width: '100%' }}
                      >
                        <TileImageComponent
                          tileOrigin={tileOrigin}
                          tile={tile}
                          setImageTop={setImageTop}
                        />

                        <div
                          className="tile_main"
                          onClick={e => {
                            checkIfLinkClick(e, tile.id, trackLinkClick);
                          }}
                        >
                          <TileTextsContainer
                            headline={tile.headline}
                            supportingContent={tile.supportingContent}
                            disclaimerText={tile.disclaimerText}
                          />
                          {tile.attachments && (
                            <AttachmentsWrapper>
                              <Attachments attachments={tile.attachments} />
                            </AttachmentsWrapper>
                          )}
                        </div>
                        {tile.isAnonymous && <AnonymousBannerComponent />}
                        <TileQuizComponent
                          tile={tile}
                          submitAnswer={submitAnswer}
                          tileOrigin={tileOrigin}
                        />
                        <ArrowWrapper $hasSidePadding>
                          {prevTile && (
                            <DirectionalButtonLeft
                              prevTile={prevTile}
                              imageTop={imageTop}
                              ref={arrowLeftRef}
                              hidden={isFirstTile}
                            />
                          )}
                          {nextTile && (
                            <DirectionalButtonRight
                              nextTile={nextTile}
                              imageTop={imageTop}
                              ref={arrowRightRef}
                              hidden={isLastTile}
                            />
                          )}
                        </ArrowWrapper>
                      </RoundedTileHolder>
                      {(tile.sharablePath || tile.exploreSharePath) && (
                        <ShareTileComponent
                          tile={tile}
                          tileOrigin={tileOrigin}
                        />
                      )}
                    </TilePreviewBlock>
                  </TilePreview>
                </TileContainer>
              </TileViewer>
            </>
          )}
        </Container>
      </div>
    </ModalContainer>
  );
};

FullSizeTile.propTypes = {
  tileOrigin: PropTypes.string,
  userData: PropTypes.shape({
    isGuestUser: PropTypes.bool,
    isEndUser: PropTypes.bool,
  }),
  tileActions: PropTypes.shape({
    copyTile: PropTypes.func,
  }),
  tile: PropTypes.shape({
    id: PropTypes.string,
    campaignUuid: PropTypes.string,
    headline: PropTypes.string,
    supportingContent: PropTypes.string,
    attachments: PropTypes.object,
    sharablePath: PropTypes.string,
    exploreSharePath: PropTypes.string,
    isAnonymous: PropTypes.bool,
    disclaimerText: PropTypes.string,
  }),
  loading: PropTypes.bool,
  closeTile: PropTypes.func,
  nextTile: PropTypes.func,
  prevTile: PropTypes.func,
  trackLinkClick: PropTypes.func,
  shouldConvertGuestAsUser: PropTypes.bool,
  submitAnswer: PropTypes.func,
  isFirstTile: PropTypes.bool,
  isLastTile: PropTypes.bool,
  arrowLeftRef: PropTypes.object,
  arrowRightRef: PropTypes.object,
};

TileOptsBar.propTypes = {
  userData: PropTypes.object,
  tileActions: PropTypes.object,
  tile: tilesProps,
};

export default FullSizeTile;
